import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  // Promise-backed app events and native mutations share the same render order.
  // An earlier snapshot must not overwrite a newer cart operation.
  #cartUpdateVersion = 0;
  #pendingQuantityUpdates = 0;
  #cartUpdateSettled = true;
  #needsCartRefresh = false;
  #controllerActive = false;
  /** @type {AbortController | null} */
  #pendingSectionRefresh = null;

  // Empty rendering removes the focused row; reconciliation may then replace
  // its fallback control. Restore only focus lost by this open drawer, using
  // the current DOM rather than retaining a potentially detached Close button.
  #captureEmptyCartFocus() {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.isDrawer ? this.closest('dialog') : null);
    const ownedFocus = dialog?.open && dialog.contains(document.activeElement);
    return () => {
      if (ownedFocus && dialog?.open && document.activeElement === document.body && dialog.querySelector('[data-cart-empty-content]')) {
        /** @type {HTMLButtonElement | null} */ (dialog.querySelector('.theme-drawer__close-button'))?.focus({ preventScroll: true });
      }
    };
  }

  /**
   * @param {string} html
   * @param {Parameters<typeof morphSection>[2]} options
   */
  async #morphCartSection(html, options) {
    const restoreFocus = this.#captureEmptyCartFocus();
    try {
      await morphSection(this.sectionId, html, options);
    } finally {
      restoreFocus();
    }
  }

  #beginCartUpdate() {
    this.#pendingSectionRefresh?.abort();
    this.#pendingSectionRefresh = null;
    this.#cartUpdateSettled = false;
    return ++this.#cartUpdateVersion;
  }

  /**
   * A late success may have committed after the newer mutation; a failure may
   * leave optimistic UI behind. Read canonical state once the newest operation
   * settles, never paint an interim read over an in-flight removal.
   * @param {number} version
   * @param {boolean} [needsRefresh]
   */
  async #completeCartUpdate(version, needsRefresh = false) {
    if (!this.#controllerActive) return;
    if (needsRefresh) this.#needsCartRefresh = true;
    if (version === this.#cartUpdateVersion) this.#cartUpdateSettled = true;
    if (!this.#cartUpdateSettled || !this.#needsCartRefresh) return;

    this.#needsCartRefresh = false;
    const recoveryVersion = this.#beginCartUpdate();
    this.#cartUpdateSettled = true;
    try {
      await this.#refreshSection(recoveryVersion, {
        mode: this.isDrawer ? 'hydration' : 'full',
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cart-items] Cart recovery failed:', error);
    }
  }

  /**
   * Fetch without committing a morph until the operation is still current.
   * @param {number} version
   * @param {{mode: 'hydration' | 'full', injectStylesheet?: boolean}} options
   */
  async #refreshSection(version, options) {
    if (!this.#controllerActive || version !== this.#cartUpdateVersion) return;
    this.#pendingSectionRefresh?.abort();
    const controller = new AbortController();
    this.#pendingSectionRefresh = controller;
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, undefined, controller.signal);
      if (!controller.signal.aborted && version === this.#cartUpdateVersion) {
        await this.#morphCartSection(html, options);
      }
    } finally {
      if (this.#pendingSectionRefresh === controller) this.#pendingSectionRefresh = null;
    }
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    const version = this.#beginCartUpdate();
    event.promise
      ?.then(async ({ detail }) => {
        if (version !== this.#cartUpdateVersion) return this.#completeCartUpdate(version, true);
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          await this.#morphCartSection(sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          await this.#refreshSection(version, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
        return this.#completeCartUpdate(version);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error);
          return this.#completeCartUpdate(version, true);
        }
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    const version = this.#beginCartUpdate();
    event.promise
      ?.then(async ({ detail }) => {
        if (version !== this.#cartUpdateVersion) return this.#completeCartUpdate(version, true);
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          await this.#morphCartSection(sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await this.#refreshSection(version, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
        return this.#completeCartUpdate(version);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error);
          return this.#completeCartUpdate(version, true);
        }
      });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#controllerActive = true;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controllerActive = false;
    // Invalidate pending snapshots belonging to this disconnected controller.
    this.#beginCartUpdate();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        const restoreFocus = this.#captureEmptyCartFocus();
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
        restoreFocus();
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const restoreFocus = this.#captureEmptyCartFocus();
    const version = this.#beginCartUpdate();
    this.#pendingQuantityUpdates++;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    const rows = this.refs.cartItemRows;
    const protectionRows = rows.filter((row) => row.dataset.cartProtectionLine !== undefined);
    const removedRows = rows.filter((row) => row.dataset.key === lineId || row.dataset.parentKey === lineId);
    const removeFinalMerchandise = this.isDrawer && quantity === 0 && protectionRows.length > 0 &&
      removedRows.some((row) => row.dataset.cartProtectionLine === undefined) &&
      rows.every((row) => protectionRows.includes(row) || removedRows.includes(row));
    // Remove known keys together, never clear a concurrent unrelated add.
    const updates = removeFinalMerchandise
      ? Object.fromEntries([...removedRows, ...protectionRows].map((row) => [row.dataset.key, 0]))
      : null;
    const removesProtection = updates !== null || (this.isDrawer && quantity === 0 &&
      this.refs.cartItemRows[line - 1]?.dataset.cartProtectionLine !== undefined);
    const cartUrl = updates ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;
    // The SDK uses this marker for its own requests. Its post-fetch middleware
    // must not re-add a removed charge while the checkbox reset is debouncing.
    const mutationUrl = removesProtection ? `${cartUrl}?provider=mule` : cartUrl;
    const body = JSON.stringify({
      // Protection/upsell apps can insert lines while this request is in flight.
      ...(updates ? { updates } : { ...(lineId ? { id: lineId } : { line }), quantity }),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: updates ? Object.keys(updates).map((id) => ({ id, quantity: 0 })) : [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    // Optimistic empty UI can be followed by navigation before removal commits.
    // Keep small removal requests alive across unload; positive writes stay as-is.
    // Browsers also enforce an aggregate keepalive budget. Admission/network
    // failures must still use the normal cart recovery below, never blind retries.
    const keepalive = quantity === 0 && new TextEncoder().encode(body).byteLength <= 64 * 1024;
    return fetch(mutationUrl, { ...fetchConfig('json', { body }), keepalive })
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          // Keep the native availability message/quantity restoration visible.
          return this.#completeCartUpdate(version);
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        if (version === this.#cartUpdateVersion) this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        if (version !== this.#cartUpdateVersion) return this.#completeCartUpdate(version, true);

        await this.#morphCartSection(parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        restoreFocus();

        this.#updateCartQuantitySelectorButtonStates();
        return this.#completeCartUpdate(version);
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        return this.#completeCartUpdate(version, true);
      })
      .finally(() => {
        if (--this.#pendingQuantityUpdates === 0) this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;
    const version = this.#beginCartUpdate();

    event.promise
      ?.then(async ({ detail }) => {
        if (version !== this.#cartUpdateVersion) return this.#completeCartUpdate(version, true);
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };
        const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

        if (cartItemsHtml) {
          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              if (version === this.#cartUpdateVersion) this.#morphCartSection(cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await this.#morphCartSection(cartItemsHtml, morphOptions);
          }
        } else {
          await this.#refreshSection(version, morphOptions);
        }

        // Provider updates have no section HTML, but their fetched render still
        // inserts cart rows. Animate those rows through the same add path.
        if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
          for (const row of this.refs.cartItemRows ?? []) {
            if (!existingKeys.has(row.dataset.key)) {
              row.classList.add('adding');
              onAnimationEnd(row, () => row.classList.remove('adding'));
            }
          }
        }

        // Update button states for all cart quantity selectors after morph.
        this.#updateCartQuantitySelectorButtonStates();
        return this.#completeCartUpdate(version);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error);
          return this.#completeCartUpdate(version, true);
        }
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
